
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const PricingGuidePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    propertyType: '',
    location: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('property_type', formData.propertyType);
    formDataToSend.append('location', formData.location);
    formDataToSend.append('lead_source', 'pricing_guide');

    try {
      const response = await fetch('https://readdy.ai/api/form/d3vhs1eg28apfrolkipg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setIsSubmitted(true);
        // Trigger PDF download
        const link = document.createElement('a');
        link.href = 'https://public.readdy.ai/ai/img_res/eaa2cf5cf83a95cb1af3d177852b5041.jpg';
        link.download = '2024-Airbnb-Photography-Pricing-Guide.pdf';
        link.click();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-20 pb-16">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-download-line text-3xl text-green-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Download Starting!</h1>
            <p className="text-xl text-gray-600 mb-8">
              Your 2024 Airbnb Photography Pricing Guide is downloading now. Check your downloads folder!
            </p>
            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What's Inside Your Guide:</h3>
              <ul className="text-left space-y-2 text-gray-700">
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> Market-specific pricing strategies</li>
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> Package comparison charts</li>
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> ROI calculations for hosts</li>
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> Seasonal pricing adjustments</li>
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> Premium service add-ons</li>
              </ul>
            </div>
            <a
              href="/"
              className="inline-block bg-blue-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Back to Homepage
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-20 pb-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                2024 Airbnb Photography 
                <span className="text-blue-600"> Pricing Guide</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Download our comprehensive pricing guide and discover how professional photography can increase your Airbnb bookings by up to 40% and boost your nightly rates.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Market Analysis</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">ROI Calculator</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Pricing Strategies</span>
                </div>
              </div>
            </div>
            <div className="lg:text-right">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20real%20estate%20photography%20pricing%20guide%20mockup%20showing%20PDF%20pages%20with%20pricing%20tables%20charts%20graphs%20modern%20clean%20design%20on%20tablet%20or%20laptop%20screen%20bright%20office%20background%20professional%20styling&width=600&height=400&seq=pricing-guide-hero&orientation=landscape"
                alt="Pricing Guide Preview"
                className="rounded-lg shadow-xl w-full max-w-md mx-auto lg:mx-0"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What You'll Discover</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Market-Specific Pricing</h3>
                    <p className="text-gray-600">Learn how to price your photography services based on local market conditions and property values.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-bar-chart-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">ROI Analysis</h3>
                    <p className="text-gray-600">See real data on how professional photos impact booking rates and nightly pricing.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-calendar-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Seasonal Strategies</h3>
                    <p className="text-gray-600">Optimize your pricing for peak seasons, holidays, and local events.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-stack-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Package Comparison</h3>
                    <p className="text-gray-600">Compare different photography packages and their impact on your bottom line.</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-gray-50 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Get Your Free Guide</h3>
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
0 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
0 focus:border-transparent text-sm"
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Type
                    </label>
                    <select
                      name="propertyType"
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
0 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select property type</option>
                      <option value="apartment">Apartment</option>
                      <option value="house">House</option>
                      <option value="condo">Condo</option>
                      <option value="villa">Villa</option>
                      <option value="studio">Studio</option>
                      <option value="multiple">Multiple Properties</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5
0 focus:border-transparent text-sm"
                      placeholder="City, State"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    Download Free Pricing Guide
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    By downloading, you agree to receive helpful tips and updates about Airbnb photography. Unsubscribe anytime.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How much does professional Airbnb photography cost?</h3>
              <p className="text-gray-600">Professional Airbnb photography typically ranges from $150-$500 depending on property size, location, and package selected. Our pricing guide breaks down costs by market and property type.</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What is the ROI of professional Airbnb photos?</h3>
              <p className="text-gray-600">Studies show professional photos can increase booking rates by 24-40% and allow hosts to charge 15-25% higher nightly rates. Most hosts recover their photography investment within 2-3 bookings.</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How often should I update my Airbnb photos?</h3>
              <p className="text-gray-600">We recommend updating photos annually or after any significant property changes. Seasonal updates can also boost bookings during peak periods.</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What's included in a typical photography package?</h3>
              <p className="text-gray-600">Standard packages include 15-25 edited high-resolution images, multiple angles of each room, exterior shots, and basic editing. Premium packages may include virtual staging and drone photography.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PricingGuidePage;
